import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { UserObj } from '@Reducers/User/Type';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch, RootStateType } from '@Store';
import Modal from '@Components/Modal';
import UploadDocument from '@Components/DocumentUpload/UploadDocument';
import DocumentUploadSuccessfully from '@Components/DocumentUpload/DocumentUploadSuccessfully';
import DocumentDelete from '@Components/DocumentUpload/DocumentDelete';
import Title from '@Components/Title';
import SbButton from '@Components/Button';

import { getKycSetting } from '@Reducers/Global';

import SingleDocument from './SingleDocument';
import { Box, styled } from '@mui/system';
import { getAccountType } from '@Services/user';
import { KycType } from '@Reducers/kyc/Type';
import { docUploadSucModalClose, getUserKyc } from '@Reducers/kyc';
import { USER_STATUS_LIST } from '@Constants/user';
import SBDropdown from '@Components/Dropdown';
import { userLogin } from '@Services/hasAdminAccess';

interface PropsType {
  open: boolean;
  data?: UserObj;
  handleClose?: () => void;
  fetchList: () => void;
}

const SingleDocumentWrapper = styled(Grid)(() => ({
  border: '1px solid #b2b2b2',
  borderRadius: '8px',
}));

function KycModal({ open, handleClose, data, fetchList }: PropsType) {
  const isUserLogin = userLogin();
  const dispatch = useDispatch<AppDispatch>();
  const kycSetting = useSelector((state: RootStateType) => state.global.kycSetting);
  const isAccountHolder = !isUserLogin
    ? data?._accountId?.isAccountHolder
    : useSelector((state: RootStateType) => state.loginUser.profile?._accountId.isAccountHolder) || false;
  const documentUploadSuccessfullyModal = useSelector(
    (state: RootStateType) => state.kyc.documentUploadSuccessfullyModal,
  );
  const _userId = data?._id || '';
  const _kyc = useSelector((state: RootStateType) => state.kyc.adminKyc?.[_userId] || {});
  const kycVerified = data?.onBoardedStatus === USER_STATUS_LIST.on_boarded.value;

  const [uploadDocumentModal, setUploadDocumentModal] = useState<boolean>(false);
  const [deleteDocumentModal, setDeleteDocumentModal] = useState<boolean>(false);
  const [successfullyModal, setSuccessfullyModal] = useState<boolean>(false);
  const [documentLabel, setDocumentLabel] = useState<string>('');
  const [documentEdit, setDocumentEdit] = useState<KycType | null>(null);
  const [deleteDocId, setDeleteDocId] = useState<string>('');
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const [systemId, setSystemId] = useState<string>('');

  const individualDocumentLists = useMemo(() => {
    if (kycSetting && data && data._accountId) {
      const type = getAccountType(data._accountId);
      switch (type) {
        case 'account-individual':
          return kycSetting.individualAccountHolder.list;
        case 'account-individual-company':
          return kycSetting.companyAccountHolder.list;
        case 'individual':
          return kycSetting.nonAccountHolder.list;
        default:
          return [];
      }
    }
    return [];
  }, [kycSetting]);

  useEffect(() => {
    if (!kycSetting) dispatch(getKycSetting());

    const isValidRequest = _userId && (isAccountHolder ? systemId : true);

    if (isValidRequest) {
      dispatch(
        getUserKyc({
          userId: _userId,
          ...(isAccountHolder ? { systemId } : {}),
        }),
      );
    }
  }, [_userId, systemId, isAccountHolder]);

  useEffect(() => {
    if (documentUploadSuccessfullyModal) {
      setSuccessfullyModal(true);
    }
  }, [documentUploadSuccessfullyModal]);

  const uploadDocumentModalClose = () => setUploadDocumentModal(false);
  const uploadDocumentModalOpen = () => setUploadDocumentModal(true);

  const successModalClose = () => {
    dispatch(docUploadSucModalClose());
    uploadDocumentModalClose();
    setSuccessfullyModal(false);
    if (_userId) {
      dispatch(getUserKyc({ userId: _userId, systemId }));
      fetchList();
    }
  };

  const onDocumentUploadModalOpen = useCallback((documentLabel: string, documentData: KycType | null): void => {
    setDocumentLabel(documentLabel);
    setDocumentEdit(documentData);

    uploadDocumentModalOpen();
  }, []);

  const deleteDocumentModalClose = (refetch: boolean) => {
    setDeleteDocId('');
    setDeleteDocumentModal(false);
    if (refetch && _userId) {
      dispatch(getUserKyc({ userId: _userId, systemId }));
      fetchList();
    }
  };

  const onDocumentDeleteModalOpen = useCallback((id: string): void => {
    setDeleteDocId(id);
    setDeleteDocumentModal(true);
  }, []);

  const filteredSystemDropdownList = useMemo(() => {
    if (!data?._systems?.length) return [];

    return loggedInUserSystemDropdownList?.filter(system => data._systems.includes(system.display));
  }, [loggedInUserSystemDropdownList, data?._systems]);

  useEffect(() => {
    if (filteredSystemDropdownList?.length > 0) {
      setSystemId(filteredSystemDropdownList[0].value);
    }
  }, [filteredSystemDropdownList]);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          {/* Left Side Titles */}
          <Grid item>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Title removeBorder removePadding title="KYC Details" />
              <Title removeBorder removePadding title={`User Account No : ${data?._accountId?.accountNumber}`} />
            </Box>
          </Grid>

          {/* Right Side Dropdown */}
          {isAccountHolder && data?._accountId?.accountType === 'company' && (
            <Grid item>
              <Typography className="font-semibold my-1">Select a system to view KYC details</Typography>
              <SBDropdown
                required
                id="company-country-id"
                placeholder="Select System"
                value={[systemId || '']}
                options={filteredSystemDropdownList}
                onChange={([value]) => {
                  setSystemId(value);
                }}
              />
            </Grid>
          )}
        </Grid>
        <UploadDocument
          open={uploadDocumentModal}
          onClose={uploadDocumentModalClose}
          documentLabel={documentLabel}
          documentData={documentEdit}
          userId={data?._id || ''}
          systemId={systemId}
        />
        <DocumentUploadSuccessfully open={successfullyModal} onClose={successModalClose} />
        <DocumentDelete
          id={deleteDocId}
          userId={_userId}
          open={deleteDocumentModal}
          onClose={deleteDocumentModalClose}
          systemId={systemId}
        />
        <SingleDocumentWrapper container item>
          {individualDocumentLists.map((kyc, index) => (
            <SingleDocument
              key={`${kyc.label}-${index}`}
              label={kyc.label}
              required={kyc.isRequired}
              documentData={_kyc?.[kyc.label] || null}
              onDocumentUploadModalOpen={onDocumentUploadModalOpen}
              onDocumentDeleteModalOpen={onDocumentDeleteModalOpen}
              fetchList={fetchList}
              accountNumber={data?._accountId?.accountNumber}
              kycVerified={kycVerified}
              systemId={systemId}
            />
          ))}
        </SingleDocumentWrapper>
        <SbButton
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '8px !important',
            mt: 5,
            mb: 1,
          }}
          className="w-full"
        >
          Close
        </SbButton>
      </Modal>
    </>
  );
}

export default KycModal;
